import React, { useEffect, useState } from "react";
import {
  getQualifications,
  getQualificationFilters
} from "../../services/tournaments";
import "./index.scss";
import Dropdown from "../../static/Dropdown/Dropdown";
import { dropdownItem } from "packages/static/Dropdown/types";
import { omitNation } from "../../helpers";

interface FilterType {
  AgeCategories: dropdownItem[];
  MatchTypes: dropdownItem[];
  PlayerTypes: dropdownItem[];
  loaded: boolean;
}

const returnValue = (filt: dropdownItem[]) => {
  return filt.length ? filt.filter(f => f.selected)[0].value : "";
};

const filtersNotEmpty = (filters: FilterType) => {
  return Object.keys(filters).some((key: string) => filters[key].length > 0);
};

const yearSelectionOpts = () => {
  let year = new Date().getFullYear() + 1;
  let yearSelectionOpts: dropdownItem[] = [];

  while (year > 2000) {
    yearSelectionOpts.push({
      value: year as number,
      label: year.toString()
    });
    year--;
  }

  return yearSelectionOpts;
};

const teamSelectionOpts = () => [
  {
    value: "false",
    label: "Individual"
  },
  {
    value: "true",
    label: "Team"
  }
];

const Qualifications = ({
  tournamentKey = "0",
  isSeniorWorldChampionship = false,
  circuitCode
}: {
  tournamentKey?: string;
  isSeniorWorldChampionship?: boolean;
  circuitCode?: string;
}) => {
  const [QualificationsData, setQualifications]: any[] = useState([]);
  const [loading, setLoadState] = useState(true);
  const [filters, setQualificationFilters] = useState<FilterType>({
    AgeCategories: [],
    MatchTypes: [],
    PlayerTypes: [],
    loaded: false
  });
  const yearOptions: dropdownItem[] = yearSelectionOpts();
  const teamOptions: dropdownItem[] = teamSelectionOpts();

  const [extraFilters, setExtraFilters] = useState<{
    year: number;
    isTeam: string;
  }>({
    year: new Date().getFullYear(),
    isTeam: "true"
  });

  useEffect(() => {
    document
      .getElementsByTagName("html")[0]
      .classList.add("print-version-qualifications");
    return () => {
      document
        .getElementsByTagName("html")[0]
        .classList.remove("print-version-qualifications");
    };
  });

  const loadFilters = () => {
    getQualificationFilters(
      isSeniorWorldChampionship
        ? {
            ...extraFilters,
            isWorldChampionship: true,
            circuitCode
          }
        : { tournamentKey }
    ).then((res: any) => {
      const filters = Object.keys(res).reduce(
        (obj, key) => {
          const excludeKeys = isSeniorWorldChampionship ? ["PlayerTypes"] : [];
          const allOption =
            key !== "AgeCategories"
              ? { label: "All", value: "", selected: true }
              : null;
          const getValue = (data: unknown[]) => {
            if (!excludeKeys.includes(key)) {
              let filters = [
                allOption,
                ...data.map((filter: any) => ({
                  label: filter.name,
                  value: filter.code,
                  selected: false
                }))
              ].filter(val => val);
              if (
                filters.length &&
                filters.every((opt: any) => opt.selected == false)
              ) {
                // @ts-ignore
                filters[0].selected = true;
              }
              return filters;
            }
            return data.map((filter: any, index) => ({
              label: filter.name,
              value: filter.code,
              selected: index === 0
            }));
          };
          obj[key] = res[key].length > 1 ? [...getValue(res[key])] : [];
          return obj;
        },
        { loaded: true }
      );
      setQualificationFilters(filters as FilterType);
    });
  };

  useEffect(() => {
    setLoadState(true);
    loadFilters();
  }, []);

  useEffect(() => {
    if (loading || !isSeniorWorldChampionship) return;
    loadFilters();
  }, [extraFilters]);

  useEffect(() => {
    if (filters.loaded) {
      setLoadState(true);
      getQualifications(
        !isSeniorWorldChampionship
          ? {
              tournamentKey,
              AgeCategories: returnValue(filters.AgeCategories),
              MatchTypes: returnValue(filters.MatchTypes),
              PlayerTypes: returnValue(filters.PlayerTypes)
            }
          : {
              ...extraFilters,
              AgeCategories: returnValue(filters.AgeCategories),
              MatchTypes: returnValue(filters.MatchTypes),
              PlayerTypes: returnValue(filters.PlayerTypes),
              isWorldChampionship: true,
              circuitCode
            }
      ).then((res: FilterType) => {
        setQualifications(res);
        setLoadState(false);
      });
    }
  }, [filters]);

  if (loading) {
    return (
      <div className="qualifications">
        <div className="loading loading--in">
          <div className="loading__giff" />
        </div>
      </div>
    );
  }

  // check whether tournament key is for a wheelchair
  const flag = tournamentKey.toLowerCase().indexOf("wc") != 0;

  return (
    <div className="qualifications-container">
      <div className={`qualifications__filters`}>
        {isSeniorWorldChampionship ? (
          <>
            <Dropdown
              className={"dropdown--year-selector"}
              options={yearOptions}
              value={
                yearOptions.filter(
                  (opt: dropdownItem) => opt.value === extraFilters.year
                )[0]
              }
              onChange={({ value: year }) => {
                // @ts-ignore
                setExtraFilters(prev => ({ ...prev, year }));
              }}
            />
            <div style={{ display: "none" }}>
              <Dropdown
                className={"dropdown--team-selector"}
                options={teamOptions}
                value={
                  teamOptions.filter(
                    (opt: dropdownItem) => opt.value === extraFilters.isTeam
                  )[0]
                }
                onChange={({ value: isTeam }) => {
                  // @ts-ignore
                  setExtraFilters(prev => ({ ...prev, isTeam }));
                }}
              />
            </div>
          </>
        ) : null}
        {filters.AgeCategories.length && flag ? (
          <>
            <Dropdown
              className={!filtersNotEmpty(filters) ? " hide" : ""}
              options={filters.AgeCategories}
              value={
                filters.AgeCategories.filter(
                  (ag: dropdownItem) => ag.selected
                )[0]
              }
              onChange={selected => {
                setQualificationFilters({
                  ...filters,
                  AgeCategories: filters.AgeCategories.map(
                    (ag: dropdownItem) => ({
                      ...ag,
                      selected: ag.label === selected.label
                    })
                  )
                });
              }}
            />
          </>
        ) : null}
        {filters.MatchTypes.length && flag ? (
          <>
            <Dropdown
              options={filters.MatchTypes}
              className={!filtersNotEmpty(filters) ? " hide" : ""}
              value={
                filters.MatchTypes.filter((mt: dropdownItem) => mt.selected)[0]
              }
              onChange={(selected: dropdownItem) => {
                setQualificationFilters({
                  ...filters,
                  PlayerTypes: filters.MatchTypes.map((mt: dropdownItem) => ({
                    ...mt,
                    selected: mt.label === selected.label
                  }))
                });
              }}
            />
          </>
        ) : null}
        {filters.PlayerTypes.length && flag ? (
          <>
            <Dropdown
              options={filters.PlayerTypes}
              className={!filtersNotEmpty(filters) ? " hide" : ""}
              value={
                filters.PlayerTypes.filter((pt: dropdownItem) => pt.selected)[0]
              }
              onChange={(selected: dropdownItem) => {
                setQualificationFilters({
                  ...filters,
                  PlayerTypes: filters.PlayerTypes.map((pt: dropdownItem) => ({
                    ...pt,
                    selected: pt.label === selected.label
                  }))
                });
              }}
            />
          </>
        ) : null}
        <div>
          <a className="btn--print btn" onClick={() => window.print()}>
            Print
          </a>
        </div>
      </div>
      <div
        className={`qualifications${
          QualificationsData && !QualificationsData.length
            ? " qualifications--no-data"
            : ""
        }`}
      >
        {QualificationsData && QualificationsData.length ? (
          QualificationsData.map((team: any, key: number) => (
            <div key={key} className="qualifications__teams">
              <h3>
                <div
                  className={`itf-flags itf-flags--${team.nationCode}`}
                ></div>
                <span>{omitNation(team.nationName)}</span>
              </h3>
              <div className="qualifications__teams-players">
                {team.captain !== null ? (
                  <>
                    <b>Captain</b>
                    <a href={team.captain.profileLink}>
                      <p className="qualifications__teams-player-name">
                        {`${team.captain.givenName} `}
                        <span className="last-name">{`${team.captain.familyName.toLowerCase()}`}</span>
                      </p>
                    </a>
                  </>
                ) : null}
                {team.players.length ? (
                  <>
                    <b>Players</b>
                    {team.players.map((player: any, key: number) => (
                      <a key={key} href={`${player.profileLink}`}>
                        <p className="qualifications__teams-player-name">
                          {`${player.givenName} `}
                          <span className="last-name">{`${player.familyName.toLowerCase()}`}</span>
                        </p>
                      </a>
                    ))}
                  </>
                ) : null}
              </div>
            </div>
          ))
        ) : (
          <p>Qualification data not available</p>
        )}
      </div>
    </div>
  );
};

export default Qualifications;
